// Client-owned filters bypass the recommendation_feedbacks DB table.
// They are passed directly as request params to the search API.
// See: https://www.notion.so/30b9be10eda880afabb1e73db2492504
//
// To add a new client-owned filter:
//   1. Add an entry to FILTER_SPECS below
//   2. Remove the corresponding key from AppStore (globalSearchObjectFilters or globalSearchBooleanFilters)
//   3. Ensure the filter is enabled in the appropriate contexts in constants/config.js

import Vue from "vue";
import { FILTER_DELIMITER } from "@/constants";

export const FILTER_SPECS = {
  humanlyEvent: { type: "array", default: [] },
  humanlyMeetings: { type: "boolean", default: null },
};

const buildDefaults = () => {
  const defaults = {};
  for (const [key, spec] of Object.entries(FILTER_SPECS)) {
    defaults[key] = Array.isArray(spec.default) ? [...spec.default] : spec.default;
  }
  return defaults;
};

const isActive = (spec, value) => {
  if (spec.type === "array") return Array.isArray(value) && value.length > 0;
  if (spec.type === "boolean") return !!value;
  return !!value;
};

const state = buildDefaults();

const getters = {
  clientOwnedFilters: (state) => state,
  activeClientOwnedFilterIds: (state) => {
    return Object.entries(FILTER_SPECS)
      .filter(([key, spec]) => isActive(spec, state[key]))
      .map(([key]) => key);
  },
  activeClientOwnedFilterValues: (state) => {
    const result = {};
    for (const [key, spec] of Object.entries(FILTER_SPECS)) {
      if (!isActive(spec, state[key])) continue;
      // Array filters use the Ids suffix to match the convention in AppStore.globalSearchQuery
      const param_key = spec.type === "array" ? `${key}Ids` : key;
      result[param_key] = state[key];
    }
    return result;
  },
  // Overlay values grouped by local filter category, used by Filters.js applyGlobalFiltersToLocal
  clientOwnedObjectFilters: (state) => {
    const result = {};
    for (const [key, spec] of Object.entries(FILTER_SPECS)) {
      if (spec.type === "array") result[key] = [...(state[key] || [])];
    }
    return result;
  },
  clientOwnedBooleanFilters: (state) => {
    const result = {};
    for (const [key, spec] of Object.entries(FILTER_SPECS)) {
      if (spec.type === "boolean") result[key] = state[key];
    }
    return result;
  },
};

const mutations = {
  setClientOwnedFilter(state, { key, value }) {
    if (!(key in FILTER_SPECS)) return;
    Vue.set(state, key, value);
  },
  syncClientOwnedFiltersFromQuery(state, query) {
    for (const [key, spec] of Object.entries(FILTER_SPECS)) {
      const param = query[key];
      if (param === undefined) continue;

      if (spec.type === "array") {
        Vue.set(state, key, param.toString().split(FILTER_DELIMITER));
      } else if (spec.type === "boolean") {
        Vue.set(state, key, param === "true" || param === true);
      }
    }
  },
  // Bulk sync from local filter objects in Filters.js
  syncClientOwnedFiltersFromLocal(state, { objectFilters, booleanFilters }) {
    for (const [key, spec] of Object.entries(FILTER_SPECS)) {
      if (spec.type === "array") {
        Vue.set(state, key, [...(objectFilters[key] || [])]);
      } else if (spec.type === "boolean") {
        Vue.set(state, key, booleanFilters[key]);
      }
    }
  },
};

export default { namespaced: true, state, getters, mutations };
